import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { hasPermission } from "../lib/utils/permissions";
import { useAuthStore } from "../context/AuthContext";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import AdminListToolbar from "../components/common/AdminListToolbar";
import ListPagination from "../components/common/ListPagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";


const HealthCamps = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const canCreate = hasPermission(user, "health-camps", "create");
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const years = useMemo(() => [...new Set(items.map((item) => String(item.year || "")).filter(Boolean))]
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true })), [items]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesYear = yearFilter === "all" || String(item.year) === yearFilter;
      const matchesSearch = !query || [item.year, item.camps, item.no_of_patients]
        .some((value) => String(value || "").toLowerCase().includes(query));
      return matchesYear && matchesSearch;
    });
  }, [items, search, yearFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const paginatedItems = filteredItems.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/health-camps");
      setItems(response.data?.data?.healthCamps || []);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load health camps");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (item) => {
    try {
      await apiClient.delete(`/health-camps/${item.id}`);
      showSuccessToast("Health camp deleted successfully");
      fetchItems();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete health camp");
    }
  };

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Health Camps</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">Manage yearly health camp statistics.</p>
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" className="rounded-lg" onClick={fetchItems}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canCreate && (

            <Button type="button" className="rounded-lg" onClick={() => navigate("/health-camps/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Add Health Camp
          </Button>

          )}
        </div>
      </div>

      <AdminListToolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Search year, camps, patients..."
        searchLabel="Search health camps"
        filteredCount={filteredItems.length}
        totalCount={items.length}
        itemLabel="health camps"
        hasActiveFilters={Boolean(search) || yearFilter !== "all"}
        onReset={() => {
          setSearch("");
          setYearFilter("all");
          setPage(1);
        }}
      >
        <Select value={yearFilter} onValueChange={(value) => {
          setYearFilter(value);
          setPage(1);
        }}>
          <SelectTrigger className="min-w-44" aria-label="Filter health camps by year">
            <SelectValue placeholder="All years" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All years</SelectItem>
            {years.map((year) => <SelectItem key={year} value={year}>{year}</SelectItem>)}
          </SelectContent>
        </Select>
      </AdminListToolbar>

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">Loading health camps...</div>
        ) : filteredItems.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">
            {items.length === 0 ? "No health camps found." : "No health camps match your filters."}
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Year</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Camps</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-slate-500">No. of Patient</th>
                  <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedItems.map((item) => (
                  <tr key={item.id}>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-800">{item.year}</td>
                    <td className="px-5 py-4 text-sm text-slate-700">{item.camps}</td>
                    <td className="px-5 py-4 text-sm text-slate-700">{item.no_of_patients}</td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate(`/health-camps/${item.id}/edit`)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <DeleteConfirmationButton onConfirm={() => handleDelete(item)}>
<Button type="button" variant="outline" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
</DeleteConfirmationButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPagination
            page={page}
            pageSize={pageSize}
            totalItems={filteredItems.length}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
            itemLabel="health camps"
          />
          </>
        )}
      </div>
    </div>
  );
};

export default HealthCamps;
